import { createGlobalStyle } from 'styled-components';
import { Theme } from '../theme';

const GlobalStyles = createGlobalStyle<{ theme: Theme }>`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    scroll-behavior: smooth;
    -webkit-text-size-adjust: 100%;
  }

  body {
    font-family: ${props => props.theme.typography.fontFamily.body};
    font-size: ${props => props.theme.typography.fontSize.base};
    font-weight: ${props => props.theme.typography.fontWeight.normal};
    line-height: ${props => props.theme.typography.lineHeight.normal};
    color: ${props => props.theme.colors.text};
    background-color: ${props => props.theme.colors.background};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
    letter-spacing: ${props => props.theme.typography.letterSpacing.normal};
    font-feature-settings: 'cv02', 'cv03', 'cv04', 'cv11';
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: ${props => props.theme.typography.fontFamily.heading};
    font-weight: ${props => props.theme.typography.fontWeight.semibold};
    line-height: ${props => props.theme.typography.lineHeight.tight};
    letter-spacing: ${props => props.theme.typography.letterSpacing.tight};
    color: ${props => props.theme.colors.ink};
    text-wrap: balance;
  }

  h1 {
    font-size: ${props => props.theme.typography.fontSize['5xl']};
    line-height: ${props => props.theme.typography.lineHeight.display};
    letter-spacing: ${props => props.theme.typography.letterSpacing.display};
  }

  h2 {
    font-size: ${props => props.theme.typography.fontSize['4xl']};
    letter-spacing: ${props => props.theme.typography.letterSpacing.display};
  }

  h3 {
    font-size: ${props => props.theme.typography.fontSize['3xl']};
  }

  h4 {
    font-size: ${props => props.theme.typography.fontSize['2xl']};
  }

  h5 {
    font-size: ${props => props.theme.typography.fontSize.xl};
  }

  h6 {
    font-size: ${props => props.theme.typography.fontSize.lg};
  }

  p {
    text-wrap: pretty;
  }

  a {
    color: ${props => props.theme.colors.primary[700]};
    text-decoration: none;
    transition: color ${props => props.theme.animation.duration.fast} ${props => props.theme.animation.easing.easeOut};

    &:hover {
      color: ${props => props.theme.colors.primary[500]};
    }
  }

  button {
    cursor: pointer;
    border: none;
    background: none;
    font-family: inherit;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  /* Focus styles for accessibility */
  :focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary[500]};
    outline-offset: 3px;
    border-radius: ${props => props.theme.borderRadius.sm};
  }

  /* Selection styles */
  ::selection {
    background-color: ${props => props.theme.colors.primary[500]};
    color: ${props => props.theme.colors.white};
  }

  /* Scrollbar styles */
  ::-webkit-scrollbar {
    width: 10px;
  }

  ::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.background};
  }

  ::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.neutral[300]};
    border: 3px solid ${props => props.theme.colors.background};
    border-radius: ${props => props.theme.borderRadius.full};

    &:hover {
      background: ${props => props.theme.colors.neutral[400]};
    }
  }

  @media (prefers-reduced-motion: reduce) {
    html {
      scroll-behavior: auto;
    }

    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }

  /* Utility classes */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .container {
    max-width: ${props => props.theme.layout.maxWidth};
    margin: 0 auto;
    padding: 0 ${props => props.theme.spacing[5]};

    @media (min-width: ${props => props.theme.breakpoints.sm}) {
      padding: 0 ${props => props.theme.spacing[8]};
    }

    @media (min-width: ${props => props.theme.breakpoints.lg}) {
      padding: 0 ${props => props.theme.spacing[10]};
    }
  }

  /* Typography Utility Classes */
  .text-light {
    font-weight: ${props => props.theme.typography.fontWeight.light};
  }

  .text-normal {
    font-weight: ${props => props.theme.typography.fontWeight.normal};
  }

  .text-medium {
    font-weight: ${props => props.theme.typography.fontWeight.medium};
  }

  .text-semibold {
    font-weight: ${props => props.theme.typography.fontWeight.semibold};
  }
`;

export default GlobalStyles;
